import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModalAction } from '../../../actions/modal';
import moment from 'moment';
import { actualValueForDropdown } from '../../../utils/common';
import Form from '../../../common/Form';
import Input from '../../../common/Form/Input';
import Select from '../../../common/Form/Select';
import { useForm } from 'react-hook-form';
import Checkbox from '../../../common/Form/Checkbox';
import DatePicker from '../../../common/Form/DatePicker';
import ButtonScogoClosedOutlined from '../../../common/Buttons/ButtonScogoClosedOutlined';
import ButtonScogoPrimary from '../../../common/Buttons/ButtonScogoPrimary';
import { motion } from 'framer-motion';
import { SiteDetails } from '../../Ticket/Modal/AddAsset';
import Customer from '../../../components/Inputs/Customer';
import Warehouse from '../../../components/Inputs/Warehouse';
import Enduser from '../../../components/Inputs/Enduser';
import RenderContact from '../../../components/Inputs/Contact';
import AssetType from '../../../components/Inputs/AssetType';
import { workingCondition } from '../../Inventory/reusableComponents/SpareAssetForm';
import { createNewAsset } from '../../../actions/inventory';

const warrantyType = [
    { key: 1, value: 'MANUFACTURING', label: 'Manufacturing' },
    { key: 2, value: 'INSTALLATION', label: 'Installation' },
    { key: 3, value: 'BILLING', label: 'Billing' },
];

const formName = 'addSpareAssets';


const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.4 } }
};

const slideIn = {
    hidden: { x: -20, opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { duration: 0.3 } }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const AddSpareAsset = ({ location, location_id, serial_number, org_id, additionalProps, onClose = () => { } }) => {
    const dispatch = useDispatch();
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);

    const defaultValues = { location_id: location_id, serial_number: serial_number, customer: org_id };

    const submit = (formValues) => {
        let owner;
        let owner_id
        if (actualValueForDropdown(formValues?.contact) === 'add-new-contact') {
            owner = {
                name: formValues[`contact:name`],
                email: formValues[`contact:email`],
                mobile: formValues[`contact:mobile`]
            }
        } else if (formValues?.contact) {
            owner_id = formValues.contact.id;
        }

        const payload = {
            asset_type_id: actualValueForDropdown(formValues.asset_type_id),
            location_id: actualValueForDropdown(formValues.location_id),
            org_id: actualValueForDropdown(formValues.customer),
            owner,
            owner_id,
            added_on: moment().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
            external_id: formValues.external_id,
            status: "AVAILABLE",
            working_condition: "NEW",
            is_temporary: false,
            is_spare: true,
            warranty_type: actualValueForDropdown(formValues.warranty_type),
            warranty_start_date: formValues.warranty_start_date,
            warranty_end_date: formValues.warranty_end_date,
            quantity: parseInt(formValues.quantity) || 1,
        };

        let additional_fields = [];
        formValues.asset_type_id.fields.forEach(field => {
            if (formValues[field.key] && formValues[field.key] !== '') {
                additional_fields.push({
                    key: field.key,
                    value: formValues[field.key]
                });
            }
        });

        payload.additional_fields = additional_fields;

        let refreshAssetList = true;
        if (location || additionalProps && additionalProps.source === 'update_rma_form') refreshAssetList = false;
        dispatch(createNewAsset({ payload, formName, onSuccess: () => { }, refreshAssetList, additionalProps }));
    };

    const onCancel = () => {
        dispatch(closeModalAction());
        onClose()
    };

    const methods = useForm({ defaultValues, mode: 'all' });
    const submitRef = useRef(null);
    const selectedOrg = methods.watch('customer');
    const selectedAssetType = methods.watch('asset_type_id');

    const serialNumber = methods.watch('serial_number');

    useEffect(() => {
        if (serialNumber) {
            methods.setValue('quantity', 1);
        } else {
            methods.setValue('quantity', '');
        }
    }, [methods, serialNumber]);

    return (
        <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="w-full h-full flex flex-col overflow-hidden max-h-[65vh]"
        >
            <div className='h-full flex-1 overflow-auto py-4'>
                <Form
                    className='flex-1 flex flex-col'
                    onSubmit={submit}
                    submitRef={submitRef}
                    methods={methods}
                >
                    <div className='flex-1 overflow-y-auto'>
                        {location && <SiteDetails location={location} type='warehouse' />}

                        <motion.div
                            className='px-3 py-4 bg-white rounded-lg shadow-sm'
                            variants={staggerContainer}
                        >
                            <motion.div
                                className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'
                                variants={staggerContainer}
                            >
                                {!org_id && (
                                    <motion.div variants={slideIn} className="col-span-1 md:col-span-2 lg:col-span-1">
                                        <Customer name='customer' label='Customer' />
                                    </motion.div>
                                )}

                                {!location_id && <motion.div variants={slideIn} className="col-span-1 md:col-span-2 lg:col-span-1">
                                    <Warehouse
                                        label='Warehouse'
                                        required
                                        name='location_id'
                                        org_id={actualValueForDropdown(selectedOrg)}
                                        prefill={location_id ? [location_id] : []}
                                    />
                                </motion.div>
                                }

                                <motion.div variants={slideIn} className="col-span-1 md:col-span-2 lg:col-span-1">
                                    <Input
                                        label='External Id'
                                        name='external_id'
                                        type={'text'}
                                        className="w-full"
                                    />
                                </motion.div>

                                <motion.div variants={slideIn} className="col-span-1 md:col-span-2 lg:col-span-1">
                                    <AssetType
                                        addNew
                                        label='Asset Type'
                                        required
                                        name='asset_type_id'
                                        org_id={actualValueForDropdown(selectedOrg)}
                                    />
                                </motion.div>

                                {selectedAssetType && selectedAssetType.fields.map((field, index) => (
                                    <motion.div key={field.key} variants={slideIn} className="col-span-1 md:col-span-2 lg:col-span-1">
                                        <Input
                                            label={field.label}
                                            name={field.key}
                                            type={'text'}
                                            className="w-full"
                                            required={field.is_required}
                                        />
                                    </motion.div>
                                ))}
                                <motion.div variants={slideIn} className="col-span-1 md:col-span-2 lg:col-span-1">
                                    <Input
                                        label='Quantity'
                                        name='quantity'
                                        type={'number'}
                                        className="w-full"
                                        required={true}
                                        shouldUnregister
                                        readOnly={methods.watch('serial_number')?.trim?.()?.length > 0}
                                    />
                                </motion.div>

                            </motion.div>

                            <motion.div
                                className='mt-2 p-3 bg-gray-50 rounded-md'
                                variants={slideIn}
                            >
                                <Checkbox
                                    label='Owner same as Site'
                                    name='hasSameOwner'
                                    id='hasSameOwner'
                                />
                            </motion.div>

                            {!methods.watch('hasSameOwner') && (
                                <motion.div
                                    className='mt-4'
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <RenderContact
                                        name={'contact'}
                                        label={'Contact Name'}
                                        org_id={actualValueForDropdown(selectedOrg)}
                                    />
                                </motion.div>
                            )}

                            <motion.div
                                className='mt-2 p-3 bg-gray-50 rounded-md'
                                variants={slideIn}
                            >
                                <Checkbox
                                    label='Do you have Warranty Details'
                                    name='hasWarranty'
                                    id='hasWarranty'
                                />
                            </motion.div>

                            {methods.watch('hasWarranty') && (
                                <motion.div
                                    className='mt-4 grid grid-cols-1 md:grid-cols-3 gap-4'
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <Select
                                        label='Type'
                                        type={'dropdown'}
                                        name='warranty_type'
                                        options={warrantyType}
                                        shouldUnregister
                                        className="bg-white w-full"
                                    />
                                    <DatePicker
                                        label='Warranty Start Date'
                                        name='warranty_start_date'
                                        dateFormat='dd-MM-yyyy'
                                        shouldUnregister
                                        className="bg-white w-full"
                                    />
                                    <DatePicker
                                        label='Warranty End Date'
                                        name='warranty_end_date'
                                        dateFormat='dd-MM-yyyy'
                                        shouldUnregister
                                        className="bg-white w-full"
                                    />
                                </motion.div>
                            )}
                        </motion.div>
                    </div>
                </Form>
            </div>
            <div className='flex flex-col sm:flex-row justify-end py-4 gap-4 border-t bg-white z-10 px-4'>
                <ButtonScogoPrimary
                    textOrComponent={'Submit'}
                    loading={formButtonLoading[formName]}
                    onCancel={() => dispatch(closeModalAction())}
                    onClick={() => {
                        submitRef?.current?.click();
                    }}
                    className="w-full sm:w-auto"
                />
                <ButtonScogoClosedOutlined
                    onClick={onCancel}
                    textOrComponent={'Cancel'}
                    className="w-full sm:w-auto"
                />
            </div>
        </motion.div>
    );
};

export default AddSpareAsset;